import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { WORD_OUTLINES } from './PhotoStandWordOutlines.js';

const linear = hex => new THREE.Color(hex).convertSRGBToLinear();
function mesh(name, geometry, material) {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}
function roundedRect(w, h, r) {
  const p = new THREE.Shape(), x = -w / 2, y = -h / 2;
  p.moveTo(x+r,y); p.lineTo(x+w-r,y); p.quadraticCurveTo(x+w,y,x+w,y+r);
  p.lineTo(x+w,y+h-r); p.quadraticCurveTo(x+w,y+h,x+w-r,y+h);
  p.lineTo(x+r,y+h); p.quadraticCurveTo(x,y+h,x,y+h-r);
  p.lineTo(x,y+r); p.quadraticCurveTo(x,y,x+r,y); p.closePath();
  return p;
}
function extrude(shape, depth, bevel=0.008) {
  return new THREE.ExtrudeGeometry(shape, {depth, steps:1, curveSegments:16,
    bevelEnabled:bevel>0, bevelSize:bevel, bevelThickness:bevel, bevelSegments:4});
}

// Physical text outlines retain the exact letter case and installed-font kerning.
function wordShapes(word, width, height) {
  const path = new THREE.ShapePath();
  for (const [op, ...v] of WORD_OUTLINES[word]) {
    if (op==='m') path.moveTo(...v);
    if (op==='l') path.lineTo(...v);
    if (op==='q') path.quadraticCurveTo(...v);
    if (op==='c') path.bezierCurveTo(...v);
    if (op==='z') path.currentPath.closePath();
  }
  const original = path.toShapes();
  const boundsGeometry = new THREE.ShapeGeometry(original,16);
  boundsGeometry.computeBoundingBox();
  const bounds=boundsGeometry.boundingBox, center=bounds.getCenter(new THREE.Vector3());
  const scale=height===undefined?width/(bounds.max.x-bounds.min.x):height/(bounds.max.y-bounds.min.y);
  const points=p=>p.getPoints(16).map(v=>new THREE.Vector2((v.x-center.x)*scale,(v.y-center.y)*scale));
  boundsGeometry.dispose();
  return original.map(shape=>{
    const copy=new THREE.Shape(points(shape));
    copy.holes=shape.holes.map(hole=>new THREE.Path(points(hole)));
    return copy;
  });
}

function woodTexture() {
  const w=512,h=256,rgba=new Uint8Array(w*h*4);
  let seed=73461;
  for(let y=0;y<h;y++)for(let x=0;x<w;x++){
    seed=(Math.imul(seed,1664525)+1013904223)>>>0;
    const noise=seed/4294967296-.5;
    const flow=y+5*Math.sin(x*.012+y*.018)+2*Math.sin(x*.027);
    const grain=Math.sin(flow*1.75)*.023+Math.sin(flow*.47)*.042;
    const band=.055*Math.sin(flow*.095)+grain+noise*.035;
    rgba.set([110*(1+band),67*(1+band),42*(1+band),255],(y*w+x)*4);
  }
  const texture=new THREE.DataTexture(rgba,w,h);
  texture.encoding=THREE.sRGBEncoding;
  texture.wrapS=texture.wrapT=THREE.RepeatWrapping;
  texture.minFilter=THREE.LinearMipmapLinearFilter;
  texture.magFilter=THREE.LinearFilter;
  texture.generateMipmaps=true;texture.needsUpdate=true;
  return texture;
}
function remapWoodUV(geometry) {
  const p=geometry.attributes.position,uv=geometry.attributes.uv;
  for(let i=0;i<p.count;i++)uv.setXY(i,(p.getX(i)+1.35)/2.7,(p.getY(i)+p.getZ(i)*.38+.5)*1.1);
  uv.needsUpdate=true;
}

function convexHull(points) {
  const sorted=points.sort((a,b)=>a.x-b.x||a.y-b.y);
  const cross=(o,a,b)=>(a.x-o.x)*(b.y-o.y)-(a.y-o.y)*(b.x-o.x);
  const half=items=>{const result=[];for(const p of items){while(result.length>1&&cross(result[result.length-2],result[result.length-1],p)<=0)result.pop();result.push(p);}return result;};
  const lo=half(sorted),hi=half([...sorted].reverse());lo.pop();hi.pop();return lo.concat(hi);
}
function polygonContains(polygon,x,y) {
  let inside=false;
  for(let i=0,j=polygon.length-1;i<polygon.length;j=i++){
    const a=polygon[i],b=polygon[j];
    if((a.y>y)!==(b.y>y)&&x<(b.x-a.x)*(y-a.y)/(b.y-a.y)+a.x)inside=!inside;
  }
  return inside;
}
function blur(values,size,radius) {
  const tmp=new Float32Array(values.length),result=new Float32Array(values.length);
  const weights=[];let sum=0;
  for(let i=-radius;i<=radius;i++){const v=Math.exp(-i*i/(radius*radius*.55));weights.push(v);sum+=v;}
  for(let y=0;y<size;y++)for(let x=0;x<size;x++){
    let v=0;for(let k=-radius;k<=radius;k++)v+=values[y*size+Math.max(0,Math.min(size-1,x+k))]*weights[k+radius];
    tmp[y*size+x]=v/sum;
  }
  for(let y=0;y<size;y++)for(let x=0;x<size;x++){
    let v=0;for(let k=-radius;k<=radius;k++)v+=tmp[Math.max(0,Math.min(size-1,y+k))*size+x]*weights[k+radius];
    result[y*size+x]=v/sum;
  }
  return result;
}

// Project the opaque base and photo card, not the transparent acrylic, onto the
// desk. The resulting unlit alpha receiver works with the site's baked materials
// even when renderer.shadowMap is off. It is exported with the model.
export function createDeskShadow(model, lightDirection=new THREE.Vector3(-3,8,5)) {
  if(lightDirection.y<=0)throw new Error('The light must be above the desk');
  model.updateMatrixWorld(true);
  const polygons=[];
  for(const name of ['WoodBody','PhotoCard']){
    const box=new THREE.Box3().setFromObject(model.getObjectByName(name));
    const points=[];
    for(const x of [box.min.x,box.max.x])for(const y of [box.min.y,box.max.y])for(const z of [box.min.z,box.max.z]){
      points.push(new THREE.Vector2(x-y*lightDirection.x/lightDirection.y,z-y*lightDirection.z/lightDirection.y));
    }
    polygons.push(convexHull(points));
  }
  const box2=new THREE.Box2().setFromPoints(polygons.flat()).expandByScalar(.44);
  const extent=box2.getSize(new THREE.Vector2()),center=box2.getCenter(new THREE.Vector2());
  const size=256,mask=new Float32Array(size*size),contact=new Float32Array(size*size);
  for(let row=0;row<size;row++)for(let col=0;col<size;col++){
    const x=box2.min.x+(col+.5)*extent.x/size,z=box2.max.y-(row+.5)*extent.y/size;
    if(polygons.some(poly=>polygonContains(poly,x,z)))mask[row*size+col]=.18;
    const dx=Math.max(0,Math.abs(x)-1.19),dz=Math.max(0,Math.abs(z)-.31);
    contact[row*size+col]=.28*Math.exp(-(dx*dx+dz*dz)/.008);
  }
  const soft=blur(mask,size,10),rgba=new Uint8Array(size*size*4);
  for(let i=0;i<soft.length;i++)rgba.set([32,26,22,Math.round(255*(1-(1-soft[i])*(1-contact[i])))],i*4);
  const texture=new THREE.DataTexture(rgba,size,size);
  // Plane UV v=0 is z=max after the -PI/2 rotation. Rows already match that
  // direction, so leave them unflipped in both WebGL and GLB export.
  texture.encoding=THREE.sRGBEncoding;texture.flipY=false;
  texture.minFilter=THREE.LinearMipmapLinearFilter;texture.magFilter=THREE.LinearFilter;
  texture.generateMipmaps=true;texture.needsUpdate=true;
  const material=new THREE.MeshBasicMaterial({map:texture,transparent:true,depthWrite:false,
    polygonOffset:true,polygonOffsetFactor:-1,polygonOffsetUnits:-1,toneMapped:false});
  const shadow=mesh('DeskShadow',new THREE.PlaneGeometry(extent.x,extent.y),material);
  shadow.rotation.x=-Math.PI/2;shadow.position.set(center.x,.006,center.y);
  shadow.castShadow=false;shadow.receiveShadow=false;
  shadow.userData.kind='portable baked contact + projected silhouette';
  return shadow;
}

export function createRefinedPhotoStand({photoTexture,includeShadow=true}={}) {
  const model=new THREE.Group();model.name='RefinedPhotoStand';
  const grain=woodTexture();
  const wood=new THREE.MeshStandardMaterial({map:grain,bumpMap:grain,bumpScale:.004,color:0xffffff,roughness:.58,metalness:0});
  const darkWood=new THREE.MeshStandardMaterial({color:linear(0x382015),roughness:.9});
  const paper=new THREE.MeshStandardMaterial({color:linear(0xfaf8f2),roughness:.84});
  const clear=new THREE.MeshPhysicalMaterial({color:linear(0xeaf5f6),roughness:.12,metalness:0,
    transmission:.24,transparent:true,opacity:.31,ior:1.49,clearcoat:1,clearcoatRoughness:.09,depthWrite:false});
  const rubber=new THREE.MeshStandardMaterial({color:linear(0x252623),roughness:.95});

  const bodyGeometry=new RoundedBoxGeometry(2.55,.42,.82,6,.055);
  const bp=bodyGeometry.attributes.position;
  for(let i=0;i<bp.count;i++){const y=bp.getY(i),z=bp.getZ(i);bp.setZ(i,z-Math.sign(z)*.16*(y+.21));}
  bodyGeometry.computeVertexNormals();remapWoodUV(bodyGeometry);
  const body=mesh('WoodBody',bodyGeometry,wood);body.position.y=.225;model.add(body);
  for(const x of [-.94,.94])for(const z of [-.25,.25]){
    const foot=mesh('RecessedFeltFoot',new THREE.CylinderGeometry(.075,.075,.015,32),rubber);
    foot.position.set(x,.0075,z);model.add(foot);
  }
  const faceGroup=new THREE.Group();faceGroup.name='EngravedFront';faceGroup.position.set(0,.225,.369);faceGroup.rotation.x=-.18;
  const faceShape=roundedRect(2.48,.34,.055),letters=wordShapes('ZhenFund',1.67);
  const islands=[];
  for(const letter of letters){
    faceShape.holes.push(new THREE.Path(letter.getPoints()));
    for(const hole of letter.holes)islands.push(new THREE.Shape(hole.getPoints()));
  }
  const faceGeometry=extrude(faceShape,.055,.003);remapWoodUV(faceGeometry);
  faceGroup.add(mesh('CarvedWoodFace',faceGeometry,wood));
  if(islands.length){const geometry=extrude(islands,.055,.002);remapWoodUV(geometry);faceGroup.add(mesh('LetterCounterIslands',geometry,wood));}
  const engraving=mesh('ZhenFundEngraving',new THREE.ShapeGeometry(letters,16),darkWood);
  engraving.position.z=.030;engraving.userData.text='ZhenFund';
  faceGroup.add(engraving);model.add(faceGroup);
  // Rotate a complete carved face, including the counter islands and recess.
  // A half-turn preserves readable lettering; negative scale would mirror it.
  const rearFace=new THREE.Group();rearFace.name='EngravedBack';rearFace.rotation.y=Math.PI;
  const rearCarving=faceGroup.clone(true);
  rearCarving.traverse(object=>{object.name=object.name+'Back';});
  rearFace.add(rearCarving);model.add(rearFace);

  const slot=mesh('PanelSlot',new RoundedBoxGeometry(2.2,.012,.255,3,.005),darkWood);
  slot.position.set(0,.44,0);model.add(slot);
  const panelShape=roundedRect(2.24,3.45,.045);
  const border=panelShape.clone();border.holes=[new THREE.Path(roundedRect(2.125,3.335,.03).getPoints(16))];
  const rim=mesh('PolishedAcrylicEdges',extrude(border,.21,.009).translate(0,0,-.105),clear);
  rim.position.set(0,2.115,0);rim.renderOrder=5;rim.castShadow=false;model.add(rim);
  const coverMaterial=clear.clone();coverMaterial.opacity=.055;coverMaterial.transmission=0;
  const coverGeometry=extrude(panelShape,.012,.003).translate(0,0,-.006);
  for(const [name,direction] of [['AcrylicFront',1],['AcrylicBack',-1]]){
    const cover=mesh(name,coverGeometry,coverMaterial);
    cover.position.set(0,2.115,direction*.105);cover.rotation.y=direction===1?0:Math.PI;
    cover.renderOrder=8;cover.castShadow=false;model.add(cover);
  }

  const card=mesh('PhotoCard',new RoundedBoxGeometry(2.025,3.235,.026,3,.007),paper);
  card.position.set(0,2.115,0);model.add(card);
  const photoMaterial=new THREE.MeshBasicMaterial({color:photoTexture?0xffffff:0x7b9cb3,
    toneMapped:false,polygonOffset:true,polygonOffsetFactor:-2,polygonOffsetUnits:-2});
  if(photoTexture){const map=photoTexture.clone();map.repeat.set(1,1);map.offset.set(0,0);map.wrapS=map.wrapT=THREE.ClampToEdgeWrapping;map.encoding=THREE.sRGBEncoding;map.minFilter=THREE.LinearMipmapLinearFilter;map.anisotropy=8;map.needsUpdate=true;photoMaterial.map=map;}
  const photoGeometry=new THREE.PlaneGeometry(1.8,2.7);
  const captionMaterial=new THREE.MeshBasicMaterial({color:linear(0x242424),toneMapped:false});
  for(const [photoName,captionName,text,direction] of [
    ['PortraitPhoto','FrontCaption','stay hungry',1],
    ['PortraitPhotoBack','BackCaption','stay foolish',-1],
  ]){
    const photo=mesh(photoName,photoGeometry,photoMaterial);
    photo.position.set(0,2.269,direction*.052);photo.rotation.y=direction===1?0:Math.PI;
    photo.renderOrder=3;photo.castShadow=false;model.add(photo);
    const caption=mesh(captionName,new THREE.ShapeGeometry(wordShapes(text,undefined,.165),16),captionMaterial);
    caption.position.set(0,.719,direction*.055);caption.rotation.y=photo.rotation.y;
    caption.userData.text=text;caption.castShadow=false;model.add(caption);
  }

  model.userData.design='Vincent Fang / symmetric double-sided photo stand / study 03';
  model.userData.photo='User original, full 2:3 portrait, no crop';
  if(includeShadow)model.add(createDeskShadow(model));
  return model;
}
